import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  RadioGroup,
  FormControlLabel,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  getCommissionSharingPolicy,
  getAllCommissionSharingPolicies,
  upsertCommissionSharingPolicy,
  listPropertyCommissionOverrides,
  deletePropertyCommissionOverride,
  upsertPropertyCommissionOverride,
  type CommissionSharingPolicy,
} from '../services/commissionService';
import type { PropertyCommissionOverride } from '../types/commission';
import { agencyService, type User } from '../services/agencyService';
import { getActiveProperties, getProperty } from '../services/propertyService';
import type { Property } from '../types/property';
import { NumericFormat } from 'react-number-format';
import Grid from '@mui/material/Grid';

interface CommissionSharingPolicyPageProps {
  showTitle?: boolean;
}

const CommissionSharingPolicyPage: React.FC<CommissionSharingPolicyPageProps> = ({ showTitle = true }) => {
  const { agencySlug } = useParams<{ agencySlug: string }>();
  const navigate = useNavigate();
  const { user, agency } = useAuth();
  const planName = (agency?.subscription?.planName || agency?.subscriptionPlan || 'Free').toLowerCase();
  const hasCommissionAccess = ['professional', 'agency premium', 'premium', 'enterprise', 'demo'].includes(planName);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [agencyPolicy, setAgencyPolicy] = useState<CommissionSharingPolicy | null>(null);
  const [teamMembers, setTeamMembers] = useState<User[]>([]);
  const [overrides, setOverrides] = useState<PropertyCommissionOverride[]>([]);
  const [deletingOverrideId, setDeletingOverrideId] = useState<string | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [editingOverride, setEditingOverride] = useState<PropertyCommissionOverride | null>(null);
  const [overrideForm, setOverrideForm] = useState({
    propertyId: '',
    agentSplitMethod: 'equal' as 'equal' | 'custom',
    customAgentPercentages: {
      primary: undefined as number | undefined,
      secondary: undefined as number | undefined,
      tertiary: undefined as number | undefined,
    },
  });
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [assigneeNames, setAssigneeNames] = useState<{
    primary?: string;
    secondary?: string;
    tertiary?: string;
  }>({});
  const [overridePropertyLoading, setOverridePropertyLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<CommissionSharingPolicy | null>(null);
  const [formData, setFormData] = useState({
    policyType: 'agency_wide' as 'agency_wide' | 'agent_specific',
    agentId: '',
    agencySharePercentage: 50,
    agentSharePercentage: 50,
    agentSplitMethod: 'equal' as 'equal' | 'custom',
    customAgentPercentages: {
      primary: undefined as number | undefined,
      secondary: undefined as number | undefined,
      tertiary: undefined as number | undefined,
    },
    agentSharePercentages: {} as Record<string, number>,
    notes: '',
  });

  useEffect(() => {
    if (agencySlug && (user?.role === 'admin' || user?.primaryRole === 'admin')) {
      loadData();
    }
  }, [agencySlug, user]);

  const loadData = async () => {
    if (!agencySlug) return;
    
    try {
      setLoading(true);
      setError(null);

      // Load team members - include all users (admins, agents, etc.)
      const members = await agencyService.getTeamMembers(agencySlug);
      setTeamMembers(members);

      // Load agency-wide policy
      const policies = await getAllCommissionSharingPolicies(agencySlug);
      const agency = policies.find(p => p.policyType === 'agency_wide' && p.isActive);
      setAgencyPolicy(agency || null);

      // Load current agency-wide policy for form
      if (agency) {
        try {
          const current = await getCommissionSharingPolicy(agencySlug);
          if (current) {
            setAgencyPolicy(current);
          }
        } catch (err) {
          // Policy might not exist yet
        }
      }

      try {
        const o = await listPropertyCommissionOverrides(agencySlug);
        setOverrides(o);
      } catch (e) {
        setOverrides([]);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (policy?: CommissionSharingPolicy) => {
    if (policy) {
      setEditingPolicy(policy);
      setFormData({
        policyType: 'agency_wide',
        agentId: '',
        agencySharePercentage: policy.agencySharePercentage,
        agentSharePercentage: policy.agentSharePercentage,
        agentSplitMethod: policy.agentSplitMethod,
        customAgentPercentages: policy.customAgentPercentages || {
          primary: undefined,
          secondary: undefined,
          tertiary: undefined,
        },
        agentSharePercentages: policy.agentSharePercentages || {},
        notes: policy.notes || '',
      });
    } else {
      setEditingPolicy(null);
      setFormData({
        policyType: 'agency_wide',
        agentId: '',
        agencySharePercentage: 50,
        agentSharePercentage: 50,
        agentSplitMethod: 'equal',
        customAgentPercentages: {
          primary: undefined,
          secondary: undefined,
          tertiary: undefined,
        },
        agentSharePercentages: {},
        notes: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPolicy(null);
  };

  const handleDeleteOverride = async (propertyId: string) => {
    if (!agencySlug) return;
    try {
      setDeletingOverrideId(propertyId);
      setError(null);
      await deletePropertyCommissionOverride(agencySlug, propertyId);
      setOverrides((prev) => prev.filter((o) => o.propertyId !== propertyId));
    } catch (err: any) {
      setError(err.message || 'Failed to delete override');
    } finally {
      setDeletingOverrideId(null);
    }
  };

  const handleOpenOverrideDialog = async (override?: PropertyCommissionOverride) => {
    if (override) {
      setEditingOverride(override);
      setOverrideForm({
        propertyId: override.propertyId,
        agentSplitMethod: override.sharing.agentSplitMethod,
        customAgentPercentages: override.sharing.customAgentPercentages ?? {
          primary: undefined,
          secondary: undefined,
          tertiary: undefined,
        },
      });
    } else {
      setEditingOverride(null);
      setOverrideForm({
        propertyId: '',
        agentSplitMethod: 'equal',
        customAgentPercentages: { primary: undefined, secondary: undefined, tertiary: undefined },
      });
      try {
        const { properties: list } = await getActiveProperties(agencySlug!, 1, 200);
        setProperties(list);
      } catch {
        setProperties([]);
      }
    }
    setOverrideDialogOpen(true);
  };

  const handleCloseOverrideDialog = () => {
    setOverrideDialogOpen(false);
    setEditingOverride(null);
    setAssigneeNames({});
  };

  const propertyIdForAssignees =
    overrideDialogOpen &&
    (overrideForm.propertyId || editingOverride?.propertyId)
      ? overrideForm.propertyId || editingOverride!.propertyId
      : null;

  useEffect(() => {
    if (!agencySlug || !propertyIdForAssignees || teamMembers.length === 0) {
      if (!propertyIdForAssignees) setAssigneeNames({});
      return;
    }
    let cancelled = false;
    setOverridePropertyLoading(true);
    getProperty(agencySlug, propertyIdForAssignees)
      .then((property) => {
        if (cancelled) return;
        const primary = property.primaryAssignee
          ? teamMembers.find((m) => m._id === property.primaryAssignee)
          : undefined;
        const secondary = property.secondaryAssignee
          ? teamMembers.find((m) => m._id === property.secondaryAssignee)
          : undefined;
        const tertiary = property.tertiaryAssignee
          ? teamMembers.find((m) => m._id === property.tertiaryAssignee)
          : undefined;
        setAssigneeNames({
          primary: primary ? `${primary.firstName} ${primary.lastName}` : undefined,
          secondary: secondary ? `${secondary.firstName} ${secondary.lastName}` : undefined,
          tertiary: tertiary ? `${tertiary.firstName} ${tertiary.lastName}` : undefined,
        });
      })
      .catch(() => {
        if (!cancelled) setAssigneeNames({});
      })
      .finally(() => {
        if (!cancelled) setOverridePropertyLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agencySlug, propertyIdForAssignees, teamMembers]);

  const handleSaveOverride = async () => {
    if (!agencySlug) return;
    const propertyId = (overrideForm.propertyId || editingOverride?.propertyId || '').trim();
    if (!propertyId) {
      setError('Select a property');
      return;
    }
    if (overrideForm.agentSplitMethod === 'custom') {
      const p = overrideForm.customAgentPercentages;
      const sum = (p.primary ?? 0) + (p.secondary ?? 0) + (p.tertiary ?? 0);
      if (sum === 0) {
        setError('Custom agent split requires at least one of primary, secondary, or tertiary percentage to be set.');
        return;
      }
      if (Math.abs(sum - 100) > 0.01) {
        setError(`Custom agent split percentages must sum to 100%. Currently ${sum.toFixed(2)}%.`);
        return;
      }
    }

    try {
      setOverrideSaving(true);
      setError(null);
      await upsertPropertyCommissionOverride(agencySlug, propertyId, {
        agentSplitMethod: overrideForm.agentSplitMethod,
        customAgentPercentages:
          overrideForm.agentSplitMethod === 'custom' ? overrideForm.customAgentPercentages : undefined,
      });
      const list = await listPropertyCommissionOverrides(agencySlug);
      setOverrides(list);
      setSuccess('Property override saved');
      handleCloseOverrideDialog();
    } catch (err: any) {
      setError(err.message || 'Failed to save override');
    } finally {
      setOverrideSaving(false);
    }
  };

  const handleSave = async () => {
    if (formData.agencySharePercentage + formData.agentSharePercentage !== 100) {
      setError('Agency share and agent share percentages must sum to 100');
      return;
    }
    if (formData.agentSplitMethod === 'custom') {
      const p = formData.customAgentPercentages;
      const sum = (p.primary ?? 0) + (p.secondary ?? 0) + (p.tertiary ?? 0);
      if (sum === 0) {
        setError('Custom agent split requires at least one of primary, secondary, or tertiary percentage to be set.');
        return;
      }
      if (Math.abs(sum - 100) > 0.01) {
        setError(`Custom agent split percentages must sum to 100%. Currently ${sum.toFixed(2)}%.`);
        return;
      }
    }

    if (!agencySlug) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await upsertCommissionSharingPolicy(agencySlug, {
        policyType: 'agency_wide',
        agencySharePercentage: formData.agencySharePercentage,
        agentSharePercentage: formData.agentSharePercentage,
        agentSplitMethod: formData.agentSplitMethod,
        customAgentPercentages: formData.agentSplitMethod === 'custom' ? formData.customAgentPercentages : undefined,
        agentSharePercentages: formData.agentSplitMethod === 'custom' ? formData.agentSharePercentages : undefined,
        isActive: true,
        notes: formData.notes,
      });

      setSuccess('Policy saved successfully');
      await loadData();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.message || 'Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  if (!hasCommissionAccess) {
    return (
      <Box p={3}>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Commission Sharing Policy
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Commission features are available on the Agency Premium plan. Upgrade your subscription
              to configure how commission is shared between the agency and agents.
            </Typography>
            <Button
              variant="contained"
              onClick={() => agencySlug && navigate(`/agencies/${agencySlug}/subscription`)}
            >
              View subscription
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const isAdmin = user?.role === 'admin' || user?.primaryRole === 'admin' || user?.primaryRole === 'app_admin';
  if (!isAdmin) {
    return (
      <Box p={3}>
        <Alert severity="error">Only admins can manage commission sharing policies.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {showTitle && (
        <>
          <Typography variant="h4" gutterBottom>
            Commission Sharing Policy
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure how commission is shared between the agency and agents. Set an agency-wide
            policy and optionally override how the agent share is split per property.
          </Typography>
        </>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Agency-wide Policy */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center">
              <BusinessIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Agency-wide Policy</Typography>
            </Box>
            <Button
              variant={agencyPolicy ? 'outlined' : 'contained'}
              startIcon={agencyPolicy ? <EditIcon /> : <AddIcon />}
              onClick={() => {
                if (agencyPolicy) {
                  // Edit existing agency-wide policy
                  handleOpenDialog(agencyPolicy);
                } else {
                  // Create new agency-wide policy
                  setEditingPolicy(null);
                  setFormData({
                    policyType: 'agency_wide',
                    agentId: '',
                    agencySharePercentage: 50,
                    agentSharePercentage: 50,
                    agentSplitMethod: 'equal',
                    customAgentPercentages: {
                      primary: undefined,
                      secondary: undefined,
                      tertiary: undefined,
                    },
                    agentSharePercentages: {},
                    notes: '',
                  });
                  setDialogOpen(true);
                }
              }}
            >
              {agencyPolicy ? 'Edit' : 'Create'}
            </Button>
          </Box>

          {agencyPolicy ? (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Agency Share: <strong>{agencyPolicy.agencySharePercentage}%</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Agent Share: <strong>{agencyPolicy.agentSharePercentage}%</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Split Method: <strong>{agencyPolicy.agentSplitMethod === 'equal' ? 'Equal' : 'Custom'}</strong>
              </Typography>
              
              {/* Show Agent Split details when custom */}
              {agencyPolicy.agentSplitMethod === 'custom' && agencyPolicy.customAgentPercentages && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Agent Split Percentages:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {agencyPolicy.customAgentPercentages.primary !== undefined && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Primary Agent (Lead/First):
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {agencyPolicy.customAgentPercentages.primary}%
                        </Typography>
                      </Box>
                    )}
                    {agencyPolicy.customAgentPercentages.secondary !== undefined && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Secondary Agent (Second):
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {agencyPolicy.customAgentPercentages.secondary}%
                        </Typography>
                      </Box>
                    )}
                    {agencyPolicy.customAgentPercentages.tertiary !== undefined && (
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Tertiary Agent (Third):
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {agencyPolicy.customAgentPercentages.tertiary}%
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    These percentages apply to the {agencyPolicy.agentSharePercentage}% agent share.
                  </Typography>
                </Box>
              )}
              
              {agencyPolicy.notes && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Notes: {agencyPolicy.notes}
                </Typography>
              )}
            </Box>
          ) : (
            <Alert severity="info">
              No agency-wide policy set. Create one to set the default commission sharing for all agents.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Property-specific overrides (agent split only) */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <HomeIcon color="action" />
              <Typography variant="h6">Property-specific overrides</Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenOverrideDialog()}
            >
              Add override
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Override how the agent share is split among assigned agents (equal or custom) for a specific property.
            Commission rate and agency vs agent % are not changed. Managed here only; the property Commission section is read-only.
          </Typography>
          {overrides.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Property</TableCell>
                    <TableCell>Agent split</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {overrides.map((o) => (
                    <TableRow key={o.propertyId}>
                      <TableCell>
                        <Typography variant="body2">{o.propertyAddress || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" display="block">
                          {o.sharing.agentSplitMethod === 'equal'
                            ? 'Equal'
                            : o.sharing.customAgentPercentages
                              ? `Custom ${[o.sharing.customAgentPercentages.primary, o.sharing.customAgentPercentages.secondary, o.sharing.customAgentPercentages.tertiary].filter((x): x is number => x != null).join('/')}`
                              : 'Custom'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenOverrideDialog(o)}
                          color="primary"
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <Button
                          size="small"
                          startIcon={<HomeIcon />}
                          onClick={() => agencySlug && navigate(`/agencies/${agencySlug}/properties/${o.propertyId}`)}
                        >
                          View
                        </Button>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteOverride(o.propertyId)}
                          disabled={deletingOverrideId === o.propertyId}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              No property-specific overrides. Click &quot;Add override&quot; to set a custom agent split
              for a property.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Property override Add/Edit dialog */}
      <Dialog open={overrideDialogOpen} onClose={handleCloseOverrideDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingOverride ? 'Edit property override' : 'Add property override'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {editingOverride ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {editingOverride.propertyAddress || editingOverride.propertyId}
              </Typography>
            ) : (
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Property</InputLabel>
                <Select
                  value={overrideForm.propertyId}
                  onChange={(e) => setOverrideForm({ ...overrideForm, propertyId: e.target.value })}
                  label="Property"
                >
                  {properties.map((p) => (
                    <MenuItem key={p._id} value={p._id}>
                      {p.address?.formattedAddress || [p.address?.street, p.address?.city].filter(Boolean).join(', ') || p._id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Override only how the agent share is split among assigned agents. Commission rate and agency vs agent % are not changed.
            </Typography>
            <FormControl component="fieldset" fullWidth>
              <Typography variant="subtitle2" gutterBottom>Agent split</Typography>
              <RadioGroup
                row
                value={overrideForm.agentSplitMethod}
                onChange={(e) =>
                  setOverrideForm({ ...overrideForm, agentSplitMethod: e.target.value as 'equal' | 'custom' })
                }
              >
                <FormControlLabel value="equal" control={<Radio size="small" />} label="Equal" />
                <FormControlLabel value="custom" control={<Radio size="small" />} label="Custom" />
              </RadioGroup>
            </FormControl>
            {overrideForm.agentSplitMethod === 'custom' && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {!propertyIdForAssignees && !editingOverride && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Select a property above to see assignees for each role.
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={4}>
                  {overridePropertyLoading ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Primary …
                    </Typography>
                  ) : assigneeNames.primary ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Primary: {assigneeNames.primary}
                    </Typography>
                  ) : propertyIdForAssignees ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Primary: (no assignee)
                    </Typography>
                  ) : null}
                  <NumericFormat
                    customInput={TextField}
                    fullWidth
                    size="small"
                    label="Primary %"
                    value={overrideForm.customAgentPercentages.primary ?? ''}
                    onValueChange={({ floatValue }) =>
                      setOverrideForm({
                        ...overrideForm,
                        customAgentPercentages: {
                          ...overrideForm.customAgentPercentages,
                          primary: floatValue,
                        },
                      })
                    }
                    decimalScale={2}
                    min={0}
                    max={100}
                    suffix="%"
                  />
                </Grid>
                <Grid item xs={4}>
                  {overridePropertyLoading ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Secondary …
                    </Typography>
                  ) : assigneeNames.secondary ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Secondary: {assigneeNames.secondary}
                    </Typography>
                  ) : propertyIdForAssignees ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Secondary: (no assignee)
                    </Typography>
                  ) : null}
                  <NumericFormat
                    customInput={TextField}
                    fullWidth
                    size="small"
                    label="Secondary %"
                    value={overrideForm.customAgentPercentages.secondary ?? ''}
                    onValueChange={({ floatValue }) =>
                      setOverrideForm({
                        ...overrideForm,
                        customAgentPercentages: {
                          ...overrideForm.customAgentPercentages,
                          secondary: floatValue,
                        },
                      })
                    }
                    decimalScale={2}
                    min={0}
                    max={100}
                    suffix="%"
                  />
                </Grid>
                <Grid item xs={4}>
                  {overridePropertyLoading ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Tertiary …
                    </Typography>
                  ) : assigneeNames.tertiary ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Tertiary: {assigneeNames.tertiary}
                    </Typography>
                  ) : propertyIdForAssignees ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Tertiary: (no assignee)
                    </Typography>
                  ) : null}
                  <NumericFormat
                    customInput={TextField}
                    fullWidth
                    size="small"
                    label="Tertiary %"
                    value={overrideForm.customAgentPercentages.tertiary ?? ''}
                    onValueChange={({ floatValue }) =>
                      setOverrideForm({
                        ...overrideForm,
                        customAgentPercentages: {
                          ...overrideForm.customAgentPercentages,
                          tertiary: floatValue,
                        },
                      })
                    }
                    decimalScale={2}
                    min={0}
                    max={100}
                    suffix="%"
                  />
                </Grid>
                {(() => {
                  const p = overrideForm.customAgentPercentages;
                  const total = (p.primary ?? 0) + (p.secondary ?? 0) + (p.tertiary ?? 0);
                  if (total > 0 && Math.abs(total - 100) > 0.01) {
                    return (
                      <Grid item xs={12}>
                        <Alert severity="error">
                          Custom agent split must sum to 100%. Currently {total.toFixed(2)}%.
                        </Alert>
                      </Grid>
                    );
                  }
                  return null;
                })()}
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOverrideDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveOverride}
            disabled={
              overrideSaving ||
              (overrideForm.agentSplitMethod === 'custom' &&
                Math.abs(
                  (overrideForm.customAgentPercentages.primary ?? 0) +
                    (overrideForm.customAgentPercentages.secondary ?? 0) +
                    (overrideForm.customAgentPercentages.tertiary ?? 0) -
                    100
                ) > 0.01)
            }
          >
            {overrideSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Policy Dialog (agency-wide only) */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPolicy ? 'Edit Agency-wide Policy' : 'Create Agency-wide Policy'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Box sx={{ mt: 0, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Commission Split
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <NumericFormat
                  customInput={TextField}
                  label="Agency Share (%)"
                  value={formData.agencySharePercentage}
                  onValueChange={(values) => {
                    const agency = values.floatValue || 0;
                    setFormData({
                      ...formData,
                      agencySharePercentage: agency,
                      agentSharePercentage: 100 - agency,
                    });
                  }}
                  decimalScale={2}
                  min={0}
                  max={100}
                  fullWidth
                  margin="normal"
                />
                <Typography variant="h6">+</Typography>
                <NumericFormat
                  customInput={TextField}
                  label="Agent Share (%)"
                  value={formData.agentSharePercentage}
                  onValueChange={(values) => {
                    const agent = values.floatValue || 0;
                    setFormData({
                      ...formData,
                      agentSharePercentage: agent,
                      agencySharePercentage: 100 - agent,
                    });
                  }}
                  decimalScale={2}
                  min={0}
                  max={100}
                  fullWidth
                  margin="normal"
                />
              </Box>
              {formData.agencySharePercentage + formData.agentSharePercentage !== 100 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Percentages must sum to 100%
                </Alert>
              )}
            </Box>

            <FormControl component="fieldset" margin="normal" fullWidth>
              <Typography variant="subtitle2" gutterBottom>
                Agent Split Method (when multiple agents)
              </Typography>
              <RadioGroup
                value={formData.agentSplitMethod}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    agentSplitMethod: e.target.value as 'equal' | 'custom',
                  })
                }
              >
                <FormControlLabel
                  value="equal"
                  control={<Radio />}
                  label="Equal split among all agents"
                />
                <FormControlLabel
                  value="custom"
                  control={<Radio />}
                  label="Custom percentages (configure per agent)"
                />
              </RadioGroup>
            </FormControl>

            {formData.agentSplitMethod === 'custom' && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Agent Position Percentages
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Specify percentages for each agent position. These percentages should sum to 100% of the agent share.
                  For example, if the agent share is 45%, and you set primary=60%, secondary=30%, tertiary=10%,
                  then primary gets 27% of total commission, secondary gets 13.5%, and tertiary gets 4.5%.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <NumericFormat
                      customInput={TextField}
                      label="Primary Agent (Lead/First) %"
                      value={formData.customAgentPercentages.primary || ''}
                      onValueChange={(values) => {
                        setFormData({
                          ...formData,
                          customAgentPercentages: {
                            ...formData.customAgentPercentages,
                            primary: values.floatValue,
                          },
                        });
                      }}
                      decimalScale={2}
                      min={0}
                      max={100}
                      fullWidth
                      margin="normal"
                      helperText="Percentage of agent share for primary assignee"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <NumericFormat
                      customInput={TextField}
                      label="Secondary Agent (Second) %"
                      value={formData.customAgentPercentages.secondary || ''}
                      onValueChange={(values) => {
                        setFormData({
                          ...formData,
                          customAgentPercentages: {
                            ...formData.customAgentPercentages,
                            secondary: values.floatValue,
                          },
                        });
                      }}
                      decimalScale={2}
                      min={0}
                      max={100}
                      fullWidth
                      margin="normal"
                      helperText="Percentage of agent share for secondary assignee"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <NumericFormat
                      customInput={TextField}
                      label="Tertiary Agent (Third) %"
                      value={formData.customAgentPercentages.tertiary || ''}
                      onValueChange={(values) => {
                        setFormData({
                          ...formData,
                          customAgentPercentages: {
                            ...formData.customAgentPercentages,
                            tertiary: values.floatValue,
                          },
                        });
                      }}
                      decimalScale={2}
                      min={0}
                      max={100}
                      fullWidth
                      margin="normal"
                      helperText="Percentage of agent share for tertiary assignee"
                    />
                  </Grid>
                </Grid>
                {(() => {
                  const total = (formData.customAgentPercentages.primary || 0) +
                                (formData.customAgentPercentages.secondary || 0) +
                                (formData.customAgentPercentages.tertiary || 0);
                  if (total > 0 && Math.abs(total - 100) > 0.01) {
                    return (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        Custom agent split percentages must sum to 100%. Currently {total.toFixed(2)}%. Save is disabled until fixed.
                      </Alert>
                    );
                  }
                  if (total === 0) {
                    return (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        Set at least one of primary, secondary, or tertiary percentage.
                      </Alert>
                    );
                  }
                  return null;
                })()}
              </Box>
            )}

            <TextField
              label="Notes (Optional)"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              margin="normal"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              saving ||
              formData.agencySharePercentage + formData.agentSharePercentage !== 100 ||
              (formData.agentSplitMethod === 'custom' &&
                Math.abs(
                  (formData.customAgentPercentages.primary ?? 0) +
                    (formData.customAgentPercentages.secondary ?? 0) +
                    (formData.customAgentPercentages.tertiary ?? 0) -
                    100
                ) > 0.01)
            }
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommissionSharingPolicyPage;
